// src/pages/AdminWorkArea.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { DEFAULT_MAP_CENTER } from "../constants/mapDefaults";
import AdminLayout from "../components/AdminLayout";
import { fetchWorkAreas } from "../actions/WorkAreaActions";
import { postWorkArea } from "../services/workAreaService";
import { 
  MapPin, 
  X, 
  Search, 
  Edit2, 
  CheckCircle, 
  XCircle,
  Plus,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Grid,
  Table,
  Navigation
} from "lucide-react";

const DEFAULT_CENTER = DEFAULT_MAP_CENTER;

// Map Click Handler Component
function MapClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng);
    },
  });
  return null;
}

function MapCenterUpdater({ center, zoom = 17 }) {
  const map = useMap();

  useEffect(() => {
    if (!center) return;
    map.setView(center, zoom);
  }, [center, zoom, map]);

  return null;
}

// Stat Card Component
function StatCard({ title, value, icon: Icon, color }) {
  const colorClasses = {
    blue: { bg: "bg-blue-50", iconBg: "bg-blue-100", text: "text-blue-600", border: "border-blue-200" },
    green: { bg: "bg-emerald-50", iconBg: "bg-emerald-100", text: "text-emerald-600", border: "border-emerald-200" },
    red: { bg: "bg-red-50", iconBg: "bg-red-100", text: "text-red-600", border: "border-red-200" },
    gray: { bg: "bg-gray-50", iconBg: "bg-gray-100", text: "text-gray-600", border: "border-gray-200" },
    purple: { bg: "bg-purple-50", iconBg: "bg-purple-100", text: "text-purple-600", border: "border-purple-200" },
  };
  
  const colors = colorClasses[color] || colorClasses.blue;
  
  return (
    <div className={`${colors.bg} border ${colors.border} rounded-lg p-3 transition-all hover:shadow-sm`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className={`${colors.iconBg} p-1.5 rounded-lg`}>
          <Icon size={14} className={colors.text} />
        </div>
      </div>
      <p className="text-xl font-semibold text-gray-900">{value}</p>
      <p className="text-xs text-gray-600 mt-0.5">{title}</p>
    </div>
  );
}

// Work Area Card Component (Mobile View)
function WorkAreaCard({ area, index, onEdit }) {
  const statusColor = area.status === "A" 
    ? "bg-emerald-50 text-emerald-700" 
    : "bg-gray-100 text-gray-600";
  const StatusIcon = area.status === "A" ? CheckCircle : XCircle;
  
  return (
    <div className="bg-white border border-gray-100 rounded-lg p-3 hover:shadow-sm transition">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-gray-50 rounded-lg">
            <MapPin size={14} className="text-gray-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{area.name}</p>
            <p className="text-xs text-gray-500 font-mono">{area.code}</p>
          </div>
        </div>
        <button
          onClick={() => onEdit(area)}
          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition"
        >
          <Edit2 size={12} />
        </button>
      </div>
      
      {/* Coordinates */}
      <div className="grid grid-cols-2 gap-2 text-xs mb-2">
        <div>
          <p className="text-gray-500">Latitude</p>
          <p className="font-medium text-gray-900">{area.lat || "—"}</p>
        </div>
        <div>
          <p className="text-gray-500">Longitude</p>
          <p className="font-medium text-gray-900">{area.lon || "—"}</p>
        </div>
      </div>
      
      {/* Status */}
      <div className="pt-2 border-t border-gray-50">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
          <StatusIcon size={10} />
          {area.status === "A" ? "Active" : "Inactive"}
        </span>
      </div>
    </div>
  );
}

// Work Area Modal Component
function WorkAreaModal({
  editingArea,
  areaCode,
  setAreaCode,
  areaName,
  setAreaName,
  areaLat,
  setAreaLat,
  areaLon,
  setAreaLon,
  areaStatus,
  setAreaStatus,
  modalLoading,
  modalError,
  modalSuccess,
  saveArea,
  setIsModalOpen,
}) {
  const [liveLocation, setLiveLocation] = useState(null);
  const [liveLocationError, setLiveLocationError] = useState("");
  const latNum = parseFloat(areaLat);
  const lonNum = parseFloat(areaLon);
  const hasValidCoords = !Number.isNaN(latNum) && !Number.isNaN(lonNum);
  const mapCenter = hasValidCoords
    ? [latNum, lonNum]
    : liveLocation
      ? [liveLocation.lat, liveLocation.lng]
      : DEFAULT_CENTER;

  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setLiveLocationError("Geolocation is not supported in this browser.");
      return undefined;
    }

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const lat = Number(position.coords.latitude);
        const lng = Number(position.coords.longitude);
        setLiveLocation({ lat, lng });
        setLiveLocationError("");
      },
      (error) => {
        setLiveLocationError(error?.message || "Unable to read live location.");
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 10000,
      }
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-gray-100 rounded-lg">
              <MapPin size={16} className="text-gray-600" />
            </div>
            <h2 className="text-sm font-semibold text-gray-900">
              {editingArea ? "Edit Work Area" : "Add Work Area"}
            </h2>
          </div>
          <button
            onClick={() => setIsModalOpen(false)}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition"
          >
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          {modalError && (
            <div className="bg-red-50 text-red-600 text-xs p-2 rounded-lg text-center border border-red-100">
              {modalError}
            </div>
          )}
          {modalSuccess && (
            <div className="bg-green-50 text-green-600 text-xs p-2 rounded-lg text-center border border-green-100">
              {modalSuccess}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); saveArea(); }} className="space-y-3">
            {/* Code */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={areaCode}
                onChange={(e) => setAreaCode(e.target.value)}
                maxLength={5}
                placeholder="e.g., WA001"
                disabled={!!editingArea}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 disabled:bg-gray-50 disabled:text-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum 5 characters allowed.</p>
            </div>

            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={areaName}
                onChange={(e) => setAreaName(e.target.value)}
                placeholder="Work area name"
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
              />
            </div>

            {/* Coordinates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Latitude <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={areaLat}
                  onChange={(e) => setAreaLat(e.target.value)}
                  placeholder="e.g., 6.9271"
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Longitude <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={areaLon}
                  onChange={(e) => setAreaLon(e.target.value)}
                  placeholder="e.g., 79.8612"
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
                />
              </div>
            </div>

            {/* Map */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Pick Location on Map
              </label>
              {liveLocationError && (
                <p className="text-xs text-amber-600 mb-1">{liveLocationError}</p>
              )}
              <div className="rounded-lg overflow-hidden border border-gray-200 h-48">
                <MapContainer
                  center={mapCenter}
                  zoom={17}
                  style={{ height: "100%", width: "100%" }}
                >
                  <MapCenterUpdater center={mapCenter} zoom={17} />
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <MapClickHandler
                    onPick={(latlng) => {
                      setAreaLat(latlng.lat.toFixed(6));
                      setAreaLon(latlng.lng.toFixed(6));
                    }}
                  />
                  {hasValidCoords && (
                    <CircleMarker
                      center={[latNum, lonNum]}
                      radius={8}
                      pathOptions={{ color: "#374151", fillColor: "#6B7280", fillOpacity: 0.8 }}
                    />
                  )}
                  {liveLocation && (
                    <CircleMarker
                      center={[liveLocation.lat, liveLocation.lng]}
                      radius={7}
                      pathOptions={{ color: "#2563EB", fillColor: "#3B82F6", fillOpacity: 0.75 }}
                    />
                  )}
                </MapContainer>
              </div>
              <div className="mt-1 flex items-center justify-between gap-2">
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Navigation size={10} />
                  Click on map to set coordinates
                </p>
                {liveLocation && (
                  <button
                    type="button"
                    onClick={() => {
                      setAreaLat(liveLocation.lat.toFixed(6));
                      setAreaLon(liveLocation.lng.toFixed(6));
                    }}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Use Live Location
                  </button>
                )}
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={areaStatus}
                onChange={(e) => setAreaStatus(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 bg-white"
              >
                <option value="A">Active</option>
                <option value="I">Inactive</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={modalLoading}
                className="px-3 py-1.5 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {modalLoading ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function AdminWorkArea() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items: areaItems, loading, error } = useSelector(
    (state) => state.workArea || { items: [], loading: false, error: null }
  );

  const [areas, setAreas] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('table');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  
  // Form state
  const [areaCode, setAreaCode] = useState("");
  const [areaName, setAreaName] = useState("");
  const [areaLat, setAreaLat] = useState("");
  const [areaLon, setAreaLon] = useState("");
  const [areaStatus, setAreaStatus] = useState("A");
  
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");
  
  const recordsPerPage = 10;

  // Fetch Work Areas
  useEffect(() => {
    dispatch(fetchWorkAreas());
  }, [dispatch]);

  useEffect(() => {
    setAreas(areaItems || []);
  }, [areaItems]);

  // Detect screen size for default view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode('card');
      } else {
        setViewMode('table');
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
  };

  // Summary Stats
  const totalCount = areas.length;
  const activeCount = areas.filter((a) => a.status === "A").length;
  const inactiveCount = totalCount - activeCount;

  // Filtering
  const filteredAreas = useMemo(() => {
    return areas.filter((a) => {
      const textMatch = 
        (a.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (a.code || "").toLowerCase().includes(searchQuery.toLowerCase());
      const statusMatch = statusFilter === "" || a.status === statusFilter;
      return textMatch && statusMatch;
    });
  }, [areas, searchQuery, statusFilter]);

  useEffect(() => setCurrentPage(1), [searchQuery, statusFilter]);

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredAreas.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredAreas.length / recordsPerPage);

  const openAdd = () => {
    setEditingArea(null);
    setAreaCode("");
    setAreaName("");
    setAreaLat("");
    setAreaLon("");
    setAreaStatus("A");
    setModalError("");
    setModalSuccess("");
    setIsModalOpen(true);
  };

  const openEdit = (area) => {
    setEditingArea(area);
    setAreaCode(area.code || "");
    setAreaName(area.name || "");
    setAreaLat(area.lat || "");
    setAreaLon(area.lon || "");
    setAreaStatus(area.status || "A");
    setModalError("");
    setModalSuccess("");
    setIsModalOpen(true);
  };

  const existingCodes = useMemo(() => {
    return areas.map((a) => (a.code || "").toLowerCase());
  }, [areas]);

  const saveArea = async () => {
    setModalError("");
    if (!areaCode.trim() || !areaName.trim()) {
      setModalError("Code and name are required.");
      return;
    }
    if (areaCode.trim().length > 5) {
      setModalError("Maximum 5 characters allowed.");
      return;
    }
    if (!areaLat.trim() || !areaLon.trim()) {
      setModalError("Latitude and Longitude are required.");
      return;
    }
    if (!editingArea && existingCodes.includes(areaCode.toLowerCase())) {
      setModalError("Work area code already exists.");
      return;
    }
    
    setModalLoading(true);
    try {
      const payload = {
        code: areaCode,
        name: areaName,
        status: areaStatus,
        lat: areaLat,
        lon: areaLon,
      };
      const result = await postWorkArea(payload);
      if (result?.statusCode === 200) {
        setModalSuccess("Saved successfully.");
        await dispatch(fetchWorkAreas());
        setTimeout(() => {
          setIsModalOpen(false);
          setModalSuccess("");
        }, 1200);
      } else {
        setModalError("Failed to save work area.");
      }
    } catch (err) {
      console.error(err);
      setModalError(err?.response?.data?.message || err.message || "Save error");
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <AdminLayout>
      {/* Header Section */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
              Work Areas
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Manage geographic work zones and operational areas
            </p>
          </div>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition shadow-sm"
          >
            <Plus size={14} />
            Add Area
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatCard title="Total Areas" value={totalCount} icon={MapPin} color="blue" />
        <StatCard title="Active" value={activeCount} icon={CheckCircle} color="green" />
        <StatCard title="Inactive" value={inactiveCount} icon={XCircle} color="gray" />
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by Code or Name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
          />
        </div>
        
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white"
          >
            <option value="">All Status</option>
            <option value="A">Active</option>
            <option value="I">Inactive</option>
          </select>
        </div>
        
        {/* View Toggle */}
        <div className="hidden sm:flex gap-1 bg-gray-100 p-0.5 rounded-lg">
          <button
            onClick={() => setViewMode('table')}
            className={`px-2.5 py-1 text-xs font-medium rounded transition ${
              viewMode === 'table' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Table size={12} className="inline mr-1" />
            Table
          </button>
          <button
            onClick={() => setViewMode('card')}
            className={`px-2.5 py-1 text-xs font-medium rounded transition ${
              viewMode === 'card' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Grid size={12} className="inline mr-1" />
            Cards
          </button>
        </div>
      </div>

      {/* Filter Summary */}
      {(searchQuery || statusFilter) && (
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-gray-500">
            Showing {filteredAreas.length} of {areas.length} work areas
          </p>
          <button
            onClick={handleClearFilters}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <X size={12} />
            Clear filters
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw size={16} className="text-gray-400 animate-spin" />
            <span className="ml-2 text-xs text-gray-500">Loading work areas...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="bg-red-50 text-red-600 text-xs p-2 rounded-lg inline-block">
              {error}
            </div>
          </div>
        ) : filteredAreas.length === 0 ? (
          <div className="text-center py-8">
            <div className="flex flex-col items-center gap-1.5">
              <MapPin size={24} className="text-gray-300" />
              <p className="text-xs text-gray-500">No work areas found</p>
              <button
                onClick={openAdd}
                className="mt-1 text-xs text-gray-900 hover:text-gray-700 underline"
              >
                Add your first work area
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Table View - Desktop */}
            <div className={`${viewMode === 'table' ? 'block' : 'hidden'} overflow-x-auto`}>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100 sticky top-0">
                  <tr>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      #
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Latitude
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Longitude
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {currentRecords.map((area, idx) => (
                    <tr key={area.id || idx} className="hover:bg-gray-50/50 transition">
                      <td className="py-2.5 px-3 text-sm text-gray-500">
                        {indexOfFirstRecord + idx + 1}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="text-sm font-mono text-gray-900">
                          {area.code}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-1.5">
                          <MapPin size={12} className="text-gray-400" />
                          <span className="text-sm text-gray-700">{area.name}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-sm text-gray-600">
                        {area.lat || "—"}
                      </td>
                      <td className="py-2.5 px-3 text-sm text-gray-600">
                        {area.lon || "—"}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          area.status === "A" 
                            ? "bg-emerald-50 text-emerald-700" 
                            : "bg-gray-100 text-gray-600"
                        }`}>
                          {area.status === "A" ? <CheckCircle size={10} /> : <XCircle size={10} />}
                          {area.status === "A" ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        <button
                          onClick={() => openEdit(area)}
                          className="inline-flex items-center gap-1 px-1.5 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition"
                        >
                          <Edit2 size={12} />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Card View - Mobile */}
            <div className={`${viewMode === 'card' ? 'block' : 'hidden'} p-3 space-y-2`}>
              {currentRecords.map((area, idx) => (
                <WorkAreaCard
                  key={area.id || idx}
                  area={area}
                  index={indexOfFirstRecord + idx + 1}
                  onEdit={openEdit}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {filteredAreas.length > 0 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">
            Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredAreas.length)} of {filteredAreas.length}
          </p>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="px-2 py-1 text-xs text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <WorkAreaModal
          editingArea={editingArea}
          areaCode={areaCode}
          setAreaCode={setAreaCode}
          areaName={areaName}
          setAreaName={setAreaName}
          areaLat={areaLat}
          setAreaLat={setAreaLat}
          areaLon={areaLon}
          setAreaLon={setAreaLon}
          areaStatus={areaStatus}
          setAreaStatus={setAreaStatus}
          modalLoading={modalLoading}
          modalError={modalError}
          modalSuccess={modalSuccess}
          saveArea={saveArea}
          setIsModalOpen={setIsModalOpen}
        />
      )}
    </AdminLayout>
  );
}
